import { getLoadingHtml } from "./htmlViews";

interface ViewPdfOptions {
  filename?: string;
}

/**
 * Opens a PDF in a new window/tab or triggers download on Android.
 * Handles platform-specific behavior for PDF viewing.
 *
 * @param fetchPdf - A function that returns a Promise resolving to an ArrayBuffer
 * @param options - Optional configuration (filename for download)
 */
export const viewPdf = async ({
  fetchPdf,
  filename = "document.pdf",
}: {
  fetchPdf: () => Promise<ArrayBuffer>;
  filename?: string;
}): Promise<void> => {
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Android: trigger download directly
  // iOS: open in same tab (allows back navigation)
  // Desktop: open in new tab
  const newWindow = isAndroid || isIOS ? null : window.open("", "_blank");

  // Show loading message (Desktop only)
  if (newWindow) {
    newWindow.document.documentElement.innerHTML = getLoadingHtml();
  }

  try {
    const arrayBuffer = await fetchPdf();
    const blob = new Blob([arrayBuffer], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    if (isAndroid) {
      // ANDROID: It doesn't have native viewer. Trigger download from parent window using anchor element
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup memory
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else if (isIOS) {
      // iOS: Open in same tab so user can use back button to return
      window.location.href = url;
    } else if (newWindow) {
      // Desktop: Navigate to blob URL in new tab
      newWindow.location.href = url;
    }
  } catch (error) {
    if (newWindow) {
      newWindow.close();
    }
    throw error;
  }
};

export const downloadPdf = (pdfBlob: Blob | null, fileName: string) => {
  if (!pdfBlob) return;
  if (navigator && navigator.msSaveBlob) {
    navigator.msSaveBlob(pdfBlob, fileName + ".pdf");
    return;
  }

  // Create a fresh object URL on every call. Reusing/revoking a long-lived
  // URL breaks subsequent downloads on iOS Safari (back/forward cache restores
  // a page with a now-revoked blob: URL, producing "WebKitBlobResource error 1").
  const url = window.URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};
